/**
 * 登录态存储层 —— 不依赖 axios / router，可被任意模块安全引用。
 *
 * 策略：
 *   登录时可选「记住登录」：
 *     勾选 -> 令牌写 localStorage，关掉浏览器再打开仍然有效，直接进入 /index
 *     不勾 -> 令牌写 sessionStorage，仅当前标签页有效
 *   无论哪种模式，再次打开都应先落到 /index，再由路由守卫静默校验令牌，
 *   只有确认失效才清空登录态回登录页。
 */

// 新 key，统一前缀避免与其它站点冲突
const K = {
    token: 'qa_token',
    tokenType: 'qa_token_type',
    refreshToken: 'qa_refresh_token'
}
// 存储模式标记：'local' | 'session'，始终记在 localStorage（跨会话保留）
const MODE_KEY = 'qa_auth_mode'
// 早期版本直接写在 storage 上的裸 key，读取时兜底兼容，避免老用户被强制重登
const LEGACY = {
    token: 'token',
    tokenType: 'tokenType',
    refreshToken: 'refreshToken'
}
const ALL_KEYS = [K.token, K.tokenType, K.refreshToken, LEGACY.token, LEGACY.tokenType, LEGACY.refreshToken]

function get(store, key) {
    try { return store.getItem(key) } catch (e) { return null }
}
function set(store, key, value) {
    try {
        if (value === null || value === undefined || value === '') store.removeItem(key)
        else store.setItem(key, value)
    } catch (e) {}
}
function del(store, key) {
    try { store.removeItem(key) } catch (e) {}
}
function storeOf(mode) {
    return mode === 'session' ? sessionStorage : localStorage
}

/** 当前是否为「记住登录」模式（默认记住） */
export function getRemember() {
    return get(localStorage, MODE_KEY) !== 'session'
}

function readKey(name) {
    const target = storeOf(getRemember() ? 'local' : 'session')
    let value = get(target, K[name])
    if (!value) {
        // 兼容：旧的裸 key 两种存储里都找一遍
        value = get(localStorage, LEGACY[name]) || get(sessionStorage, LEGACY[name])
    }
    return value || ''
}

export function getToken() {
    return readKey('token')
}

export function getTokenType() {
    return readKey('tokenType') || 'Bearer'
}

export function getRefreshToken() {
    return readKey('refreshToken')
}

/** 是否已持有访问令牌 */
export function hasToken() {
    return !!getToken()
}

/**
 * 保存登录令牌
 * @param {Object} auth 后端登录/刷新返回体 { accessToken, tokenType, refreshToken }
 * @param {Boolean} remember 是否记住登录，true 存 localStorage
 */
export function saveAuth(auth, remember) {
    if (!auth) return
    const mode = remember === false ? 'session' : 'local'
    const target = storeOf(mode)
    const other = storeOf(mode === 'local' ? 'session' : 'local')

    // 模式切换时清理另一侧与旧 key，保证同一时刻只有一份有效登录态
    ALL_KEYS.forEach(key => del(other, key))
    ALL_KEYS.forEach(key => del(target, key))
    set(localStorage, MODE_KEY, mode)

    if (auth.accessToken) set(target, K.token, auth.accessToken)
    set(target, K.tokenType, auth.tokenType || 'Bearer')
    if (auth.refreshToken) set(target, K.refreshToken, auth.refreshToken)
}

/** 清空登录态（两侧存储 + 模式标记一并清掉） */
export function clearAuth() {
    [localStorage, sessionStorage].forEach(store => ALL_KEYS.forEach(key => del(store, key)))
    del(localStorage, MODE_KEY)
}

export default { getToken, getTokenType, getRefreshToken, hasToken, getRemember, saveAuth, clearAuth }
